// src/services/chatApi.js
import i18n from 'i18next';

// Helper to get authentication headers
const getAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Always include Content-Type for consistency in POST/DELETE requests.
    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
        // This log is very useful for debugging.
        console.warn("Auth token not found in localStorage. Request will be sent without Authorization header.");
    }
    
    return headers;
};

export const getFormDataAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Start with an empty headers object
    const headers = {};

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Notice: We NEVER add Content-Type here.
    // The browser will handle it for FormData.
    return headers;
};

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({
            detail: `Request failed with status ${response.status}`
        }));
        throw new Error(errorData.detail || 'Request failed');
    }
    
    // For 204 No Content
    if (response.status === 204) {
        return null;
    }
    
    return response.json();
};

// A new helper function to handle 401 errors globally
const handleUnauthorized = () => {
    // This is a robust way to handle expired sessions.
    console.error("Authentication failed (401). Session may be expired. Redirecting to login.");
    // Clear potentially bad tokens from storage.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token'); // If you use refresh tokens
    // Redirect the user to the login page.
    window.location.href = '/login';
};


// const API_BASE_URL = 'http://localhost:8000/api/ai-tools';

// const API_USERS_URL = 'http://localhost:8000/api/users';

// const API_BASE_URL = '/api/ai-tools';
// const API_USERS_URL = '/api/users';

const DOMAIN = process.env.REACT_APP_API_URL || '';

// Construct the full URL: https://api.ugotai.com/api/ai-tools
const API_BASE_URL = `${DOMAIN}/ai-tools`;
const API_USERS_URL = `${DOMAIN}/users`;

let isRefreshing = false;
// This queue will hold all API calls that fail while a token refresh is in progress.
let failedQueue = [];

// --- Helper function to process the queue ---
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error); // Reject all queued requests if refresh fails
        } else {
            prom.resolve(token); // Resolve all queued requests with the new token
        }
    });
    failedQueue = []; // Clear the queue
};




// A wrapper for fetch to handle errors consistently
export const apiFetch = async (url, options = {}) => {
    // 1. Prepare headers and add the current access token
    const token = localStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // 2. Make the initial API call
    const response = await fetch(url, { ...options, headers });

    // 3. Check for a 401 Unauthorized error (expired access token)
    if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            handleUnauthorized(); // No refresh token, must log out
            return Promise.reject(new Error('Unauthorized: No refresh token'));
        }

        // If a refresh is already happening, queue this request and wait
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({
                    resolve: (newToken) => {
                        // When the refresh is done, this will be called.
                        // We retry the original request with the new token.
                        const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newToken}` } };
                        resolve(apiFetch(url, newOptions)); // Recursively call apiFetch to re-process
                    },
                    reject,
                });
            });
        }

        // This is the first request to fail, so let's start the refresh process
        isRefreshing = true;

        try {
            // 4. Send the request to your refresh token endpoint
            // !!! IMPORTANT: Adjust this URL to match your Django project's refresh token URL !!!
            const refreshResponse = await fetch(`${API_BASE_URL}/users/token/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });

            if (!refreshResponse.ok) {
                // If the refresh token is invalid (e.g., expired or blacklisted), logout.
                throw new Error('Refresh token is invalid.');
            }

            const { access: newAccessToken } = await refreshResponse.json();
            
            // 5. Store the new token and process the queue
            localStorage.setItem('access_token', newAccessToken);
            processQueue(null, newAccessToken); // Process queued requests with the new token

            // 6. Retry the original failed request with the new token
            const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newAccessToken}` } };
            return apiFetch(url, newOptions); // Retry the original request

        } catch (error) {
            // If the refresh process fails, reject all queued requests and log out.
            processQueue(error, null);
            handleUnauthorized();
            return Promise.reject(error);
        } finally {
            // 7. ALWAYS unlock the refreshing state
            isRefreshing = false;
        }
    }

    // --- If the response was not 401, handle it normally ---
    if (!response.ok) {
        // This handles all other errors (400, 404, 500, etc.)
        const errorData = await response.json().catch(() => ({ detail: `Request failed with status ${response.status}` }));
        throw new Error(errorData.detail || 'An unknown API error occurred');
    }

    // For DELETE or other requests that might not return a JSON body
    if (response.status === 204) {
        return {}; // Return an empty object for consistency
    }

    return response.json(); // Return the parsed JSON for successful requests
};


// Create a new chat session
export const createChat = async () => {
    return apiFetch(`${API_BASE_URL}/chat/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: 'New Chat' }),
    });
};

// Get all chats for the current user
export const getUserChats = async () => {
    return apiFetch(`${API_BASE_URL}/chat/list/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
};

// Get all messages for a specific chat
export const getChatMessages = async (chatId) => {
    return apiFetch(`${API_BASE_URL}/chat/${chatId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
};

// Delete a chat
export const deleteChat = async (chatId) => {
    return apiFetch(`${API_BASE_URL}/chat/${chatId}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
};

export const updateMessage = async (chatId, messageIndex, newContent) => {
    return apiFetch(`${API_BASE_URL}/chat/${chatId}/update-message/${messageIndex}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content: newContent }),
    });
};

// sendMessageStream remains a special case because it handles its own response stream
export const sendMessageStream = async ({ chatId, 
    content, 
    user_id, 
    web_search, 
    systemPrompt, 
    onChunk,
    onCitations, 
    onDone, 
    onError}) => {
    try {
        if (!user_id) throw new Error('User ID is required.');

        const body = { content, web_search, user_id };
        if (systemPrompt) body.system_prompt = systemPrompt;

        const response = await fetch(`${API_BASE_URL}/chat/${chatId}/send/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            if (response.status === 401) {
                handleUnauthorized();
            }
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to send message');
        }

        // ... your streaming logic (processText) remains the same ...
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processText = ({ done, value }) => {
            if (done) { console.log('Stream finished.'); return; }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop();
            for (const part of parts) {
                if (part.startsWith('data: ')) {
                    try {
                        const rawJsonString = part.substring(6); // Extract the JSON string
                        console.log("Raw SSE data part:", part); // Log the raw part
                        console.log("Parsed JSON object:", rawJsonString); // Log the string before parsing
                        
                        const jsonData = JSON.parse(rawJsonString);
                        console.log("Parsed JSON object:", jsonData); // Log the parsed object
                        
                        if (jsonData.content) {
                            console.log("Processing content chunk:", jsonData.content.substring(0, 20) + "..."); // Log first 20 chars
                            onChunk(jsonData.content);
                        }
//                        if (jsonData.done) onDone(jsonData);
                        if (jsonData.citations) {
                            console.log("Processing citations:", jsonData.citations); // Log received citations
                            onCitations(jsonData.citations); // This calls the ChatWindow's onCitations
                        }
                        if (jsonData.done) {
                            console.log("Processing done message:", jsonData); // Log done message
                            onDone(jsonData);
                        }
                        if (jsonData.error) throw new Error(jsonData.error);
                    } catch (e) { console.error('Error parsing stream data:', e, "Data was:", part); }
                }
            }
            reader.read().then(processText);
        };
        reader.read().then(processText);

    } catch (error) {
        console.error("Streaming API call failed:", error);
        onError(error.message);
    }
};
export const optimizePrompt = async (messages) => {
    return apiFetch(`${API_BASE_URL}/optimize-prompt/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages }), // Send the messages array
    });
};


// Ask doc tool

export const uploadDocument = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const accessToken = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/ask-doc/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            // 'Content-Type' is set automatically by the browser for FormData
        },
        body: formData,
    });
    
    if (!response.ok) {
        // Handle errors like in apiFetch
        const errorData = await response.json();
        throw new Error(errorData.detail || 'File upload failed');
    }
    return response.json();
};


export const startDocumentUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        throw new Error("You must be logged in.");
    }

    const response = await fetch(`${API_BASE_URL}/ask-doc/upload/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
    });
    
    if (response.status !== 202) {
        const errorData = await response.json().catch(() => ({ 
            detail: `Request failed with status ${response.status}` 
        }));
        throw new Error(errorData.detail || 'Failed to start document processing.');
    }
    
    return response.json(); // Will return { job_id: "..." }
};

// NEW function to poll for the upload/processing status
export const getDocumentUploadStatus = async (jobId) => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        throw new Error("Authentication token not found.");
    }

    const response = await fetch(`${API_BASE_URL}/ask-doc/upload/status/${jobId}/`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        console.error(`Polling for doc upload failed with status ${response.status}`);
        return null; // Don't crash the app, just let the poll try again
    }

    return response.json();
};



export const askDocument = async (collectionName, question, historyForApi, systemPromptForApi) => {
    return apiFetch(`${API_BASE_URL}/ask-doc/ask/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            collection_name: collectionName,
            question: question,
            chat_history: historyForApi,
            system_prompt: systemPromptForApi

        }),
    });
};

export const deleteDocumentSession = async (collectionName) => {
    return apiFetch(`${API_BASE_URL}/ask-doc/delete/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ collection_name: collectionName }),
    });
};

export const createOneTimePaymentSession = (amount) => {
    return apiFetch(`${API_BASE_URL}/stripe/create-one-time-payment/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ amount }),
    });
};

export const getUserProfile = async () => {
    return apiFetch(`${API_USERS_URL}/profile/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
};

export const updateUserProfile = async (profileData) => {
    console.log('📤 Sending profile update:', profileData);
    
    try {
        const data = await apiFetch(`${API_USERS_URL}/profile/`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(profileData),
        });
        
        console.log('✅ Profile update response:', data);
        return data;
    } catch (error) {
        console.error('❌ Profile update failed:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        throw error; // Re-throw to be caught by handleUpdate
    }
};

export const deleteUserAccount = async () => {
    const response = await fetch(`${API_USERS_URL}/profile/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        if (response.status === 401) handleUnauthorized();
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete account' }));
        throw new Error(errorData.detail);
    }
    return { success: true };
};

export const cancelSubscription = async () => {
    return apiFetch(`${API_USERS_URL}/subscription/cancel/`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
};

export const getCountryList = async (language) => {
    // This endpoint doesn't require authentication, so we can use a simpler fetch
    // We'll use apiFetch for consistency in error handling, but without auth headers
    return apiFetch(`${API_USERS_URL}/countries/?lang=${language}`, {
        method: 'GET',
    });
};

export const getLitBotReferences = async () => {
    return apiFetch(`${API_BASE_URL}/litbot/references/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
};

// Send a question to the LitBot and get an answer with citations
export const askLitBot = async (question, historyForApi) => {
    return apiFetch(`${API_BASE_URL}/litbot/ask/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            question: question,
            chat_history: historyForApi,
        }),
    });
};





/**
 * Uploads a document and a language preference to the backend for summarization.
 *
 * @param {File} file - The file object (PDF, TXT, DOCX) to be summarized.
 * @param {string} language - The two-letter language code (e.g., 'en', 'de', 'fr').
 * @returns {Promise<object>} A promise that resolves to the API response, 
 *                            containing the summary and new credit balance.
 */
export const summarizeDocument = async (file, language) => {
    // 1. Create a FormData object to handle the file upload.
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);

    // 2. Get the access token for authentication.
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        // This prevents the request from even being sent if the user isn't logged in.
        console.error("Authentication token not found.");
        throw new Error("You must be logged in to perform this action.");
    }

    // 3. Use raw `fetch` because our `apiFetch` helper is designed for JSON.
    //    `FormData` requires the browser to set the 'Content-Type' header automatically.
    const response = await fetch(`${API_BASE_URL}/summarize-document/`, { // Ensure this matches your urls.py
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            // DO NOT set 'Content-Type': 'multipart/form-data'. 
            // The browser needs to set it automatically with a boundary.
        },
        body: formData,
    });

    // 4. Handle potential errors from the response.
    if (!response.ok) {
        if (response.status === 401) {
            handleUnauthorized(); // Your existing helper for expired sessions
            throw new Error('Unauthorized');
        }
        // Try to parse a detailed error message from the backend.
        const errorData = await response.json().catch(() => ({ 
            detail: `Request failed with status ${response.status}` 
        }));
        throw new Error(errorData.detail || 'An unknown error occurred during summarization.');
    }
    
    // 5. If successful, parse and return the JSON response.
    return response.json();
};

// NEW function to poll for the result
export const getSummaryStatus = async (jobId) => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        throw new Error("Authentication token not found.");
    }

    // This uses our standard apiFetch helper if you have one, or raw fetch
    const response = await fetch(`${API_BASE_URL}/summarize-document/status/${jobId}/`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        if (response.status === 401) {
            handleUnauthorized();
        }
        // It's okay if a single poll fails; we don't want to throw a big error.
        // The polling loop will just try again.
        console.error(`Polling failed for job ${jobId} with status ${response.status}`);
        return null; // Return null to indicate a failed poll attempt
    }

    return response.json();
};

// Paraphraser

export const paraphraseText = async (messages) => {
    return apiFetch(`${API_BASE_URL}/paraphrase/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages }),
    });
};

// ... other API functions

// For synchronous text translation
export const translateText = async (text, targetLanguage) => {
    return apiFetch(`${API_BASE_URL}/translate/text/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            text: text,
            target_language: targetLanguage
        }),
    });
};

// To start the asynchronous document translation job
export const startDocumentTranslation = async (file, targetLanguage) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_language', targetLanguage);

    const accessToken = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/translate/document/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData,
    });

    if (response.status !== 202) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to start job.' }));
        throw new Error(errorData.detail);
    }
    return response.json(); // Returns { job_id: "..." }
};

// To poll for the document translation status
export const getDocumentTranslationStatus = async (jobId) => {
    return apiFetch(`${API_BASE_URL}/translate/document/status/${jobId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
};

export const markTranslationRetrieved = async (translationId) => {
    return apiFetch(`${API_BASE_URL}/translations/${translationId}/mark-retrieved/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}), // Empty body
    });
};


export const generateTableOfContents = async (formData) => {
    const language = i18n.language.split('-')[0]; 
    return apiFetch(`${API_BASE_URL}/content/generate-toc/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            ...formData,
            language: language  // Add the language to the form data
        }),
    });
};

// To start the long-running full content generation
export const startFullContentGeneration = async (jobId, updatedTocText) => {
    // This is NOT a fetch call, it's a POST
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/content/start-full-generation/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
            job_id: jobId,
            table_of_contents_text: updatedTocText,
            language: language
        }),
    });
    if (response.status !== 202) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start generation.');
    }
    return response.json();
};

// To poll for status
export const getContentGenerationStatus = async (jobId) => {
    return apiFetch(`${API_BASE_URL}/content/status/${jobId}/`);
};

export const markContentRetrieved = async (contentId) => {
    return apiFetch(`${API_BASE_URL}/content/${contentId}/mark-retrieved/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}), // Empty body
    });
};

export const downloadContentFile = async (contentJson, format, topic) => {
    const language = i18n.language.split('-')[0];
    const accessToken = localStorage.getItem('access_token');
    
    const response = await fetch(`${API_BASE_URL}/content/download/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            content_json: contentJson,
            format: format, // 'docx' or 'pdf'
            topic: topic.replace(/\s+/g, '_'), // Sanitize topic for filename
            language: language
        }),
    });

    if (!response.ok) {
        // Try to parse error from backend
        const errorData = await response.json().catch(() => ({ detail: `Download failed with status ${response.status}` }));
        throw new Error(errorData.detail);
    }

    // Handle the file blob
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${topic.replace(/\s+/g, '_')}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};


// generate article

// ==================== PROJECT MANAGEMENT ====================
/*
export const createArticleProject = async (projectData) => {
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({...projectData, language: language}),
    });
    
    return handleResponse(response);
};
*/

export const createArticleProject = async (projectData) => {
    const language = i18n.language.split('-')[0]; // e.g., 'en', 'de', 'fr'
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
        body: JSON.stringify(projectData), // Send only project data
    });

    return handleResponse(response);
};

export const listArticleProjects = async () => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};

export const getProjectStatus = async (projectId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};

export const deleteArticleProject = async (projectId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};

// ==================== DOCUMENT UPLOAD ====================

export const uploadDocuments = async (projectId, formData) => {
    // We don't need to pass language separately anymore, it will be in formData
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/upload/`, {
        method: 'POST',
        headers: getFormDataAuthHeaders(),
        body: formData, // formData now contains both files and language
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({
            detail: `Upload failed with status ${response.status}`
        }));
        throw new Error(errorData.detail || 'Upload failed');
    }
    
    return response.json(); 
};
// ==================== SECTION MANAGEMENT ====================

export const addSection = async (projectId, sectionData) => {
    const language = i18n.language.split('-')[0];
    // sectionData.append('language', language);
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
        body: JSON.stringify(sectionData),
    });
    
    return handleResponse(response);
};

export const updateSection = async (projectId, sectionId, updates) => {
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/${sectionId}/`, {
        method: 'PUT',
        headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
        body: JSON.stringify(updates),
    });
    
    return handleResponse(response);
};

export const deleteSection = async (projectId, sectionId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/${sectionId}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};

export const reorderSections = async (projectId, sectionOrders) => {
    // sectionOrders should be array of {section_id, new_order}
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/reorder/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sections: sectionOrders }),
    });
    
    return handleResponse(response);
};

// ==================== ARTICLE GENERATION ====================

export const generateArticle = async (projectId, config) => {
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/generate/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(config),
    });
    
    return handleResponse(response);
};

export const downloadArticle = async (projectId, format) => {
    
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/download/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ format }),
    });
    
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({
            detail: `Download failed with status ${response.status}`
        }));
        throw new Error(errorData.detail || 'Download failed');
    }
    
    return response.blob();
};

/*

// chatApi.js
export const downloadArticle = async (projectId, format) => {
    const accessToken = localStorage.getItem('access_token');

    // Create a temporary form to trigger download
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${API_BASE_URL}/article-generator/projects/${projectId}/download/`;
    form.style.display = 'none';

    // Add auth header via hidden input (Django CSRF not needed for API)
    const tokenInput = document.createElement('input');
    tokenInput.name = 'auth_token';
    tokenInput.value = accessToken;
    form.appendChild(tokenInput);

    const formatInput = document.createElement('input');
    formatInput.name = 'format';
    formatInput.value = format;
    form.appendChild(formatInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
};
*/
// ==================== TITLE SUGGESTIONS ====================



export const suggestTitles = async (projectId) => {
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/suggest-titles/`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
    });
    
    return handleResponse(response);
};

// ==================== ADVANCED FEATURES ====================

export const enhancePrompt = async (projectId, sectionId, currentPrompt) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/${sectionId}/enhance-prompt/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ current_prompt: currentPrompt }),
    });
    
    return handleResponse(response);
};

export const regenerateSection = async (projectId, sectionId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/${sectionId}/regenerate/`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};

// ==================== PROJECT TEMPLATES ====================
/*
export const saveAsTemplate = async (projectId, templateName) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/save-template/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ template_name: templateName }),
    });
    
    return handleResponse(response);
};

export const loadFromTemplate = async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/templates/${templateId}/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};
*/
// ==================== POLLING HELPER ====================

/**
 * Poll for project status until it reaches a terminal state
 * @param {string} projectId - The project ID to poll
 * @param {function} onUpdate - Callback function called with project data on each poll
 * @param {number} interval - Polling interval in ms (default 3000)
 * @returns {function} - Function to stop polling
 */
export const pollProjectStatus = (projectId, onUpdate, interval = 3000) => {
    const pollInterval = setInterval(async () => {
        try {
            const project = await getProjectStatus(projectId);
            onUpdate(project);
            
            // Stop polling if reached terminal state
            if (['READY', 'COMPLETED', 'FAILED'].includes(project.status)) {
                clearInterval(pollInterval);
            }
        } catch (error) {
            console.error('Polling error:', error);
            // Don't clear interval on error - retry
        }
    }, interval);
    
    // Return function to stop polling
    return () => clearInterval(pollInterval);
};

// Learning assistant

export const createLearningAssistant = async (data) => {
    const language = i18n.language.split('-')[0];
    const response = await fetch(`${API_BASE_URL}/learning-assistant/`, {
        method: 'POST',
       headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create learning assistant');
    }

    return response.json();
};

// generate quiz

export const generateQuiz = async (formData) => {
    const language = i18n.language.split('-')[0];
    try {
        formData.append('language', language);
        
        const response = await fetch(`${API_BASE_URL}/quiz/generate/`, {
            method: 'POST',
            headers: getFormDataAuthHeaders(),
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate quiz');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating quiz:', error);
        throw error;
    }
};

export const checkQuizStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/quiz/status/${taskId}/`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check quiz status');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking quiz status:', error);
        throw error;
    }
};

export const downloadQuiz = async (quizzes, format) => {
    try {
        const response = await fetch(`${API_BASE_URL}/quiz/download/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                quizzes: quizzes,
                format: format
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to download quiz');
        }

        return response.blob(); // Return blob for file download
    } catch (error) {
        console.error('Error downloading quiz:', error);
        throw error;
    }
};

// motivation letter

// Add these functions to your src/services/chatApi.js file

export const generateMotivationLetter = async (formData) => {
    const language = i18n.language.split('-')[0];
    try {
        formData.append('language', language);
        
        const response = await fetch(`${API_BASE_URL}/motivation-letter/generate/`, {
            method: 'POST',
            headers: getFormDataAuthHeaders(),
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate motivation letter');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating motivation letter:', error);
        throw error;
    }
};

export const checkMotivationLetterStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/motivation-letter/status/${taskId}/`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check motivation letter status');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking motivation letter status:', error);
        throw error;
    }
};

export const downloadMotivationLetter = async (coverLetter, format) => {
    try {
        const response = await fetch(`${API_BASE_URL}/motivation-letter/download/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                cover_letter: coverLetter,
                format: format
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to download motivation letter');
        }
        
        return response.blob(); // Return blob for file download
    } catch (error) {
        console.error('Error downloading motivation letter:', error);
        throw error;
    }
};

// generate mind map

// Add these functions to your src/services/chatApi.js file

export const generateMindMap = async (formData) => {
    const language = i18n.language.split('-')[0];
    try {
        formData.append('language', language);
        
        const response = await fetch(`${API_BASE_URL}/mindmap/generate/`, {
            method: 'POST',
            headers: getFormDataAuthHeaders(),
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate mind map');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating mind map:', error);
        throw error;
    }
};

export const checkMindMapStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/mindmap/status/${taskId}/`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check mind map status');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking mind map status:', error);
        throw error;
    }
};

export const downloadMindMapImage = async (mindMapStructure, rootTitle, showColors) => {
    try {
        const response = await fetch(`${API_BASE_URL}/mindmap/download-image/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                mind_map_structure: mindMapStructure,
                root_title: rootTitle,
                show_colors: showColors
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to download mind map image');
        }
        
        return response.blob();
    } catch (error) {
        console.error('Error downloading mind map image:', error);
        throw error;
    }
};

export const downloadMindMapJSON = async (mindMapStructure, rootTitle) => {
    try {
        const jsonData = {
            root_title: rootTitle,
            structure: mindMapStructure
        };
        
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { 
            type: 'application/json' 
        });
        
        return blob;
    } catch (error) {
        console.error('Error creating JSON blob:', error);
        throw error;
    }
};


// ======= generate wordcloud =========

export const generateWordcloud = async (formData) => {
    const language = i18n.language.split('-')[0];
    try {
        formData.append('language', language);
        const response = await fetch(`${API_BASE_URL}/wordcloud/generate/`, {
            method: 'POST',
            headers: getFormDataAuthHeaders(),
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate wordcloud');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating wordcloud:', error);
        throw error;
    }
};

// ==== generate qr code ========

export const generateQRCode = async (data) => {
    const language = i18n.language.split('-')[0];
    try {
        const response = await fetch(`${API_BASE_URL}/qrcode/generate/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ data, language })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate QR code');
        }
        
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error generating QR code:', error);
        throw error;
    }
};

export const searchArxiv = async (data) => {
    const language = i18n.language.split('-')[0];
    try {
        const response = await fetch(`${API_BASE_URL}/arxiv/search/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ ...data, 
                language  })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to search ArXiv');
        }
        
        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error searching ArXiv:', error);
        throw error;
    }
};

// powerpoint to gif

export const convertPptxToGif = async (formData) => {
    const language = i18n.language.split('-')[0];
    try {
        const response = await fetch(`${API_BASE_URL}/pptx-to-gif/convert/`, {
            method: 'POST',
             headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language  // Add the language header
            },
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to convert PowerPoint to GIF');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error converting PowerPoint to GIF:', error);
        throw error;
    }
};

export const checkPptxGifStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/pptx-to-gif/status/${taskId}/`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check conversion status');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking conversion status:', error);
        throw error;
    }
};


// ================ generate image tool ==================

export const generateImage = async (prompt, imageFormat = 'PNG') => {
    const language = i18n.language.split('-')[0];
    try {
        const response = await fetch(`${API_BASE_URL}/image/image-generation/generate/`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Accept-Language': language 
                    },
            body: JSON.stringify({
                prompt: prompt,
                image_format: imageFormat
            })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate image');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error generating image:', error);
        throw error;
    }
};

export const checkImageStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/image/image-generation/status/${taskId}/`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check image status');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking image status:', error);
        throw error;
    }
};

// edit image

export const editImage = async (payload) => {
    // payload expects: { prompt, input_image, style_reference, image_format }
    const language = i18n.language.split('-')[0];
    try {
        const response = await fetch(`${API_BASE_URL}/image/image-generation/edit-image/`, { // Adjust URL
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Accept-Language': language,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to start image edit');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error editing image:', error);
        throw error;
    }
};


// ========== ask image ==============================

// Add this to imports if not already present

export const askImageStream = async (payload) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    
    // Note: We do NOT use await response.json() here because it's a stream
    const response = await fetch(`${API_BASE_URL}/image/image-generation/ask-image/`, { // Adjust URL
        method: 'POST',
         headers: {
                ...getAuthHeaders(),
                'Accept-Language': language,
                'Content-Type': 'application/json'
            },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect to stream');
    }

    return response; // Return the full response object to read the body
};

// ================= add text to image ========

export const addTextToImage = async (imageFile, textElements) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('text_elements', JSON.stringify(textElements));

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/add-text/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }

        return await response.json();
    } catch (error) {
        console.error('Error adding text:', error);
        throw error;
    }
};

// ============ remove backgound image ===================

export const removeBackground = async (imageFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/remove-background/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }

        return await response.json();
    } catch (error) {
        console.error('Error removing background:', error);
        throw error;
    }
};

// ============= sharpen image ===============

export const sharpenImage = async (imageFile, strength, radius) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('strength', strength);
    formData.append('radius', radius);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/sharpen/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }

        return await response.json();
    } catch (error) {
        console.error('Error sharpening image:', error);
        throw error;
    }
};

// ========== enhance colors ============

export const enhanceColors = async (imageFile, saturation, brightness, contrast) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('saturation', saturation);
    formData.append('brightness', brightness);
    formData.append('contrast', contrast);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/enhance/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error enhancing image:', error);
        throw error;
    }
};

// ============ denoise image ================

export const denoiseImage = async (imageFile, method, strength) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('method', method);
    formData.append('strength', strength);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/denoise/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error denoising image:', error);
        throw error;
    }
};

// =========== rsize image ==========
export const resizeImage = async (imageFile, params) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('width', params.width);
    formData.append('height', params.height);
    formData.append('method', params.method || 'Bilinear');

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/resize/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to resize image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error resizing image:', error);
        throw error;
    }
};

// ========= crop image ========

export const cropImage = async (imageFile, params) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('x', params.x);
    formData.append('y', params.y);
    formData.append('w', params.width);
    formData.append('h', params.height);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/crop/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error cropping image:', error);
        throw error;
    }
};
// ============= round inage ===========
export const roundImage = async (imageFile, params) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('mode', params.mode);
    
    if (params.mode === 'corners') {
        formData.append('radius', params.radius);
    } else {
        formData.append('x', params.x);
        formData.append('y', params.y);
        formData.append('size', params.size); // This is diameter (width)
    }

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/round/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error rounding image:', error);
        throw error;
    }
};

// ===============  convert image ===============

export const convertImage = async (imageFile, targetFormat) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('format', targetFormat);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/convert/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to convert image');
        }
        return await response.json();
    } catch (error) {
        console.error('Error converting image:', error);
        throw error;
    }
};


export const getTaskStatus = async (taskId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/image/image-generation/status/${taskId}/`, { // Adjust URL path if needed
            method: 'GET',
            headers: {
                ...getAuthHeaders() // Standard JSON headers
            }
        });
        if (!response.ok) throw new Error("Failed to check status");
        return await response.json();
    } catch (error) {
        console.error("Polling error:", error);
        throw error;
    }
};
// ============== metadata image =============

export const getMetadata = async (imageFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('image', imageFile);

    try {
        const response = await fetch(`${API_BASE_URL}/image/image-tools/metadata/`, {
            method: 'POST',
            headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to extract metadata');
        }
        return await response.json();
    } catch (error) {
        console.error('Error getting metadata:', error);
        throw error;
    }
};

/*
export const listTemplates = async () => {
    const response = await fetch(`${API_BASE_URL}/article-generator/templates/`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};


export const createProjectFromTemplate = async (templateId, projectData) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/templates/${templateId}/create-project/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(projectData),
    });
    
    return handleResponse(response);
};

export const deleteTemplate = async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/article-generator/templates/${templateId}/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    
    return handleResponse(response);
};
*/

// ==================== BATCH OPERATIONS ====================

export const bulkUpdateSections = async (projectId, updates) => {
    // updates should be array of {section_id, name?, prompt?, content?}
    const response = await fetch(`${API_BASE_URL}/article-generator/projects/${projectId}/sections/bulk-update/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ updates }),
    });
    
    return handleResponse(response);
};





export default {
    createArticleProject,
    listArticleProjects,
    getProjectStatus,
    deleteArticleProject,
    uploadDocuments,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    generateArticle,
    downloadArticle,
    suggestTitles,
    enhancePrompt,
    regenerateSection,
    pollProjectStatus,
    bulkUpdateSections
};
